// TODAY'S VERSION (v1), kept beside the v1.1 one until the press (lib/release/next).
// Copied from main as it was on 2026-10-23; everyone who is not a test account sees this one.
"use client";

// ConnectGuideModal — THE connect window, wherever it opens from (netti's chips
// during the walk, the connections list, a connection's name).
//
// It is THE sheet (EtherModal, law_sheet_rises): it rises from the bottom edge
// and holds one height for its whole life, so moving between the six subjects
// never makes it jump. Its head is the connection, once (the tool's mark, its
// name, its live state) with the navigator under it: the subjects as the
// dashboard's own words,
//
//   name · connect · permissions · instructions · import · settings
//
// Each subject says in one line what it is for (its title only when the
// navigator's word does not already say it), shows the real screens as a strip
// you can drag, arrow through, or press a picture to bring it forward, then
// gives plain numbered steps with the thing to copy under the step that asks
// for it. Every word of the navigator is a door; ← → and a swipe move between
// the subjects, and the foot's back and next do the same. The foot is the one
// action, bottom right; there are no second arrows beside it.
//
// SETTINGS exists only once there is a connection to set (ConnectionSettings):
// what it may read of you (the private ring), anything it asked for, what it
// has been sent, what it remembers of the other end, and letting it go. A tool
// with nothing minted has no settings chip, so the ring's controls never
// appear for an app the person has not named or let in.
//
// The connection's state lives in ONE place: beside its name in the head
// (live · active · quiet · waiting), read against a ticking clock. Import hands
// over to its own window (two sheets never stack): this one leaves first.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import BrandAvatar from "../BrandAvatar";
import EtherModal, { type SheetShare } from "../ether/EtherModal";
import type { MiniNavProps } from "../os/MiniNav";
import { Field, Chevron, PillButton } from "../ui";
import ImportSteps from "./ImportSteps";
import ConnectionSettings from "./ConnectionSettings.v1";
import { useRing } from "../you/useRing";
import { useHDragScroll } from "../os/useHDragScroll";
import { MCP_URL, PROMPT_SYNC, type ConnectTool, type GuidePhase, type GuideShot } from "../../lib/connect/tools";
import { PROMPT_PROJECT } from "../../lib/onboarding/prompts";
import { connState, suggestLabel, type Connection, type ConnState } from "../../lib/connect/connections";
import { memoryLine } from "../../lib/connect/memory";
import { pasteText, wiresItself } from "../../lib/connect/setup.v1";
import { arrivalBaseline, arrivalOf, type ArrivalBaseline } from "../../lib/connect/setup";
import { renameConnection } from "../../lib/connect/api";
import { COACH_FOR_TOOL, useExtension } from "../../lib/extension/useExtension";
import { isShotReady, preloadGuideShots, preloadShot } from "../../lib/connect/guideShots";
import { trackFunnel, EVENTS } from "../../lib/analytics";
import { COPY_FAILED_LINE, copyText, selectNodeText } from "../../lib/clipboard";
import "./connect-sheet.css";

const FLOW = "connect_guide";

const GENERIC_TRUST = [
  "the first time innernet runs, choose \"always allow\"",
];
const GENERIC_IMPORT = [
  "find Export data in your tool's settings",
  "download the file when it arrives",
  "bring it in with the button below",
];

// the order of the chips IS the order of the steps. settings comes last, and
// only for a connection that exists.
type Phase = "name" | GuidePhase | "settings";
const PHASES: { key: Phase; label: string }[] = [
  { key: "name", label: "name" },
  { key: "mcp", label: "connect" },
  { key: "permissions", label: "permissions" },
  { key: "instructions", label: "instructions" },
  { key: "import", label: "import" },
  { key: "settings", label: "settings" },
];
/** the sheet's exit, before the host unmounts it (EtherModal's EXIT_MS, 260, + a frame) */
const LEAVE_MS = 280;

const STATE_WORD: Record<ConnState, string> = {
  live: "live",
  active: "active",
  quiet: "quiet",
  waiting: "waiting for its first call",
};

/** a clock that ticks, so `live` decays on screen without a data change. */
function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

function CopyBlock({ text, mono = true, what, label, whole = false }: { text: string; mono?: boolean; what: string; label?: string; whole?: boolean }) {
  // "copied" only after the clipboard actually took it; a refusal says so and
  // selects the text, so the person's own copy shortcut finishes the job
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pre = useRef<HTMLPreElement>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const copied = state === "copied";
  return (
    <>
      <div className="cm-block" data-label={label || undefined}>
        {label && <span className="cm-block-label">{label}</span>}
        {/* `whole`: a config block the person must paste entire is shown entire,
            never cut by the step's height cap with its link scrolled away. */}
        <pre ref={pre} style={{ fontFamily: mono ? "var(--font-mono)" : "inherit", ...(whole ? { maxHeight: "none" } : {}) }}>{text}</pre>
        <button
          type="button"
          className="cm-block-copy"
          data-on={copied || undefined}
          onClick={() => {
            void copyText(text).then((ok) => {
              if (!ok) selectNodeText(pre.current);
              setState(ok ? "copied" : "failed");
              if (timer.current) clearTimeout(timer.current);
              timer.current = setTimeout(() => setState("idle"), ok ? 1800 : 7000);
              if (ok) trackFunnel(EVENTS.connect_instructions_copied, { what, flow: FLOW });
            });
          }}
        >
          {copied ? "copied ✓" : "copy"}
        </button>
      </div>
      {state === "failed" && <p className="cm-where" role="status"><span aria-hidden>↳</span> {COPY_FAILED_LINE}</p>}
    </>
  );
}

/** One picture of the strip. Its box is reserved by the figure (a fixed
 *  height and aspect ratio), so nothing moves while it decodes; a picture the
 *  window already warmed (lib/connect/guideShots) paints at once, and one that
 *  is still on its way fades in when it lands rather than painting in pieces. */
function ShotImg({ src, alt }: { src: string; alt: string }) {
  const [ready, setReady] = useState(() => isShotReady(src));
  useEffect(() => {
    if (ready) return;
    let alive = true;
    void preloadShot(src).then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [src, ready]);
  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      loading="eager"
      decoding="async"
      data-ready={ready || undefined}
      onLoad={() => setReady(true)}
    />
  );
}

/** THE PICTURES — a horizontal strip across the top of a step. Drag it, use
 *  the arrows, or press a picture to bring it to the front. The picture in
 *  front lights its step below, and pressing a step brings its picture. */
function ShotStrip({
  shots,
  active,
  onActive,
}: {
  shots: GuideShot[];
  active: number;
  onActive: (i: number) => void;
}) {
  const track = useRef<HTMLDivElement | null>(null);
  const { onPointerDown, dragging, didDrag } = useHDragScroll(track);
  const [edge, setEdge] = useState({ start: true, end: shots.length < 2 });

  const figAt = (i: number) => track.current?.querySelectorAll<HTMLElement>(".cg-shot")[i] ?? null;
  const bring = useCallback((i: number) => {
    const el = track.current;
    const fig = figAt(i);
    if (!el || !fig) return;
    const pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    el.scrollTo({ left: Math.max(0, fig.offsetLeft - pad), behavior: "smooth" });
  }, []);

  // the picture nearest the strip's leading edge is the one "in front".
  useEffect(() => {
    const el = track.current;
    if (!el) return;
    let raf = 0;
    const read = () => {
      raf = 0;
      const figs = Array.from(el.querySelectorAll<HTMLElement>(".cg-shot"));
      let best = 0;
      let bestD = Infinity;
      const pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
      figs.forEach((f, i) => {
        const d = Math.abs(f.offsetLeft - pad - el.scrollLeft);
        if (d < bestD) { bestD = d; best = i; }
      });
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      setEdge({ start: el.scrollLeft < 4, end: atEnd });
      // scrolled all the way: the last picture is the one being looked at
      onActive(atEnd && el.scrollLeft > 4 ? figs.length - 1 : best);
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(read); };
    read();
    el.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => { el.removeEventListener("scroll", on); window.removeEventListener("resize", on); if (raf) cancelAnimationFrame(raf); };
  }, [onActive]);

  return (
    <div className="cg-strip" data-single={shots.length < 2 || undefined}>
      <div
        ref={track}
        className="cg-strip-track"
        // a sideways strip of its own: the sheet lets its wheel through and
        // never reads its drag as a step between connections
        data-hscroll
        data-dragging={dragging || undefined}
        onPointerDown={onPointerDown}
      >
        {shots.map((sh, i) => (
          <figure
            key={sh.src}
            className="cg-shot"
            data-on={i === active || undefined}
            onClick={() => { if (!didDrag()) bring(i); }}
          >
            <ShotImg src={sh.src} alt={sh.alt} />
            <span className="cg-shot-n" aria-hidden>{sh.step}</span>
          </figure>
        ))}
      </div>
      {shots.length > 1 && (
        <>
          <button type="button" className="cg-strip-arrow" data-dir="prev" aria-label="previous picture"
            disabled={edge.start} onClick={() => bring(Math.max(0, active - 1))}>
            <Chevron size={14} dir="left" />
          </button>
          <button type="button" className="cg-strip-arrow" data-dir="next" aria-label="next picture"
            disabled={edge.end} onClick={() => bring(Math.min(shots.length - 1, active + 1))}>
            <Chevron size={14} />
          </button>
        </>
      )}
      {/* the strip exposes bring() to its steps through a custom event */}
      <StripBridge bring={bring} />
    </div>
  );
}

// the steps list lives outside the strip; it asks for a picture by step number.
const BRING_EVENT = "innernet:guide-bring";
function StripBridge({ bring }: { bring: (i: number) => void }) {
  useEffect(() => {
    const on = (e: Event) => bring((e as CustomEvent<number>).detail);
    window.addEventListener(BRING_EVENT, on as EventListener);
    return () => window.removeEventListener(BRING_EVENT, on as EventListener);
  }, [bring]);
  return null;
}

/** The pictures for one subject (on top), then its plain numbered steps. */
function GuideSteps({
  items,
  shots = [],
  under,
}: {
  items: React.ReactNode[];
  shots?: GuideShot[];
  /** something that belongs under step N (1-based): the copy block, a button. */
  under?: Record<number, React.ReactNode>;
}) {
  const [front, setFront] = useState(0);
  const litStep = shots[front]?.step ?? null;
  return (
    <>
      {shots.length > 0 && <ShotStrip shots={shots} active={front} onActive={setFront} />}
      <ol className="cm-steps cg-steps">
        {items.map((s, i) => {
          const n = i + 1;
          const shotIdx = shots.findIndex((sh) => sh.step === n);
          return (
            <li key={i} data-lit={shots.length > 1 && litStep === n ? "" : undefined}>
              <span className="cm-step-n">{n}</span>
              <span className="cm-step-body">
                {shotIdx >= 0 && shots.length > 1 ? (
                  <button type="button" className="cg-step-link"
                    onClick={() => window.dispatchEvent(new CustomEvent(BRING_EVENT, { detail: shotIdx }))}>
                    {s}
                  </button>
                ) : (
                  <span>{s}</span>
                )}
                {under?.[n]}
              </span>
            </li>
          );
        })}
      </ol>
    </>
  );
}

const Check = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5 10 17.5 19 7" />
  </svg>
);

interface Subject {
  key: Phase;
  /** what the screen is called; empty when the head or the navigator already says it */
  title: string;
  /** one short line under the title: what this subject is for */
  why?: React.ReactNode;
  body: React.ReactNode;
  /** the single primary action (advance, or perform + advance); none on settings */
  primary?: { label: string; onClick: () => void; loading?: boolean; done?: boolean };
  /** a subject that can be passed over offers it here, beside back */
  skip?: { label: string; onClick: () => void };
}

export default function ConnectGuideModal({
  tool,
  connections,
  connection = null,
  accountName,
  onClose,
  onChanged,
  onConnected,
}: {
  tool: ConnectTool;
  /** every connection already minted for THIS tool. */
  connections: Connection[];
  /** open ON a particular connection (its name was pressed in the list). */
  connection?: Connection | null;
  accountName?: string | null;
  onClose: () => void;
  onChanged: () => void;
  /** the first call landed (also broadcast as `innernet:connected`). */
  onConnected?: (c: Connection) => void;
}) {
  const titleId = useId();
  // the sheet plays its way out before the host unmounts it
  const [open, setOpen] = useState(true);
  const leaving = useRef<ReturnType<typeof setTimeout> | null>(null);
  const close = useCallback(() => {
    setOpen(false);
    if (!leaving.current) leaving.current = setTimeout(onClose, LEAVE_MS);
  }, [onClose]);
  useEffect(() => () => { if (leaving.current) clearTimeout(leaving.current); }, []);

  // the connection this window is about: the one it was opened on, else the
  // tool's newest still-waiting one (resume), else nothing yet (mint).
  const [minted, setMinted] = useState<Connection | null>(
    () => connection ?? connections.find((c) => !c.last_seen_at) ?? null,
  );
  // the freshest copy of it — the list above is live, this window reads it.
  const current = useMemo(
    () => (minted ? connections.find((c) => c.id === minted.id) ?? minted : null),
    [minted, connections],
  );
  const [seenLocal, setSeenLocal] = useState(false);
  const seen = seenLocal || !!current?.last_seen_at;
  const [label, setLabel] = useState(() => minted?.label ?? suggestLabel(tool.id, accountName, connections));
  // an established connection opens on its settings; a waiting one on its wiring
  const [section, setSection] = useState<Phase>(() =>
    minted ? (minted.last_seen_at ? "settings" : "mcp") : "name",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gone, setGone] = useState(false);
  const [importing, setImporting] = useState(false);
  const now = useNow(20_000);
  // the private ring, for the settings section (hidden when it is not open to
  // this person: the route answers 404 and the section shows the rest)
  const ring = useRing();

  // ONE link for every connection (2026-10-24). The name is kept on the row
  // minted here, and the tool's first call claims it (migration 122's
  // connection_arrive: the one waiting row of that tool). Links already
  // pasted with a tag keep working at the door; none are handed out.
  const endpoint = MCP_URL;
  const config = useMemo(() => tool.config.split(MCP_URL).join(endpoint), [tool.config, endpoint]);
  const deeplink = useMemo(() => (tool.deeplink ? tool.deeplink(endpoint) : null), [tool, endpoint]);
  const name = current?.label ?? label;
  const toolName = tool.generic ? "your tool" : tool.name;

  // every picture this tool's guide shows, across every subject, fetched and
  // decoded now, so moving between subjects never waits on one
  useEffect(() => {
    void preloadGuideShots(tool);
  }, [tool]);

  useEffect(() => {
    trackFunnel(EVENTS.connect_client_selected, { client: tool.id, flow: FLOW, via: connection ? "list_open" : "guide" });
  }, [tool.id, connection]);

  // ── name: mint, or rename ───────────────────────────────────────────────
  const save = useCallback(async (): Promise<boolean> => {
    const next = label.trim();
    if (!next) { setError("give it a name, anything you'll recognise"); return false; }
    setBusy(true); setError(null);
    try {
      if (minted) {
        if (next !== (current?.label ?? minted.label)) {
          const res = await fetch("/api/connections", {
            method: "PATCH", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: minted.id, label: next }),
          });
          if (!res.ok) throw new Error("could not rename that");
          setMinted({ ...minted, label: next });
          onChanged();
        }
        return true;
      }
      const res = await fetch("/api/connections", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool_id: tool.id, label: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) throw new Error("you need to be signed in to name a connection");
      if (!res.ok) throw new Error(data?.error ?? "could not save that");
      setMinted(data.connection as Connection);
      onChanged();
      // a new app: the ring learns of it on its next read
      void ring.reload();
      trackFunnel(EVENTS.onboarding_step_completed, { step: `mint.${tool.id}`, index: 0, flow: FLOW, client: tool.id });
      return true;
    } catch (e) {
      const reason = e instanceof Error ? e.message : "could not save that";
      setError(reason);
      trackFunnel(EVENTS.connect_failed, { client: tool.id, reason, flow: FLOW });
      return false;
    } finally {
      setBusy(false);
    }
  }, [label, minted, current, tool.id, onChanged, ring]);

  // leaving the name chip saves the name on the way out.
  const go = useCallback((next: Phase) => {
    if (section === "name" && next !== "name" && label.trim() && label.trim() !== (current?.label ?? "")) {
      void save().then((ok) => { if (ok) setSection(next); });
      return;
    }
    setSection(next);
  }, [section, label, current, save]);

  // ── let go — archived, never erased (asked twice in the settings) ─────────
  const letGo = useCallback(async () => {
    if (!minted || gone) return;
    setGone(true);
    try {
      await fetch(`/api/connections?id=${encodeURIComponent(minted.id)}`, { method: "DELETE" });
    } catch { /* the list refetches either way */ }
    onChanged();
    close();
  }, [minted, gone, onChanged, close]);

  // ── the watch — from the moment it is named, on every subject ──────────
  // Its own row first: the first call of this tool claims it. When it does not
  // (a second waiting row of the same tool, a tool that names itself unlike
  // its catalogue entry), the arrival lands as a row of its own; any FIRST call
  // of this tool after the watch began is that arrival (by id, never by clock,
  // lib/connect/setup arrivalOf). It takes the name given here and the waiting
  // row is let go, so the person never sees two.
  const watchFrom = useRef<ArrivalBaseline | null>(null);
  useEffect(() => {
    if (!minted || seen) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async () => {
      try {
        const res = await fetch("/api/connections", { cache: "no-store" });
        if (res.ok && !disposed) {
          const data = (await res.json()) as { connections?: Connection[] };
          const all = data.connections ?? [];
          // the first answer is the baseline: nothing that had called by then is an arrival
          if (!watchFrom.current) watchFrom.current = arrivalBaseline(all);
          let mine = all.find((c) => c.id === minted.id);
          if (!mine?.last_seen_at) {
            const others = all.filter((c) => c.tool_id === tool.id && c.id !== minted.id);
            const came = arrivalOf(watchFrom.current, others);
            if (came?.tier === 1) {
              const name = mine?.label ?? minted.label;
              const row = came.row;
              // an arrival innernet named on its own takes the person's name; one they named keeps theirs
              const renamed = row.adopted_at && row.label !== name ? await renameConnection(row.id, name) : false;
              await fetch(`/api/connections?id=${encodeURIComponent(minted.id)}`, { method: "DELETE" }).catch(() => undefined);
              mine = renamed ? { ...row, label: name } : row;
            }
          }
          if (!disposed && mine?.last_seen_at) {
            setSeenLocal(true);
            setMinted(mine);
            onChanged();
            trackFunnel(EVENTS.connect_verified, { client: tool.id, via: "guide_poll", flow: FLOW });
            window.dispatchEvent(new CustomEvent("innernet:connected", { detail: { tool: tool.id, id: mine.id } }));
            onConnected?.(mine);
            return;
          }
        }
      } catch { /* offline — keep waiting */ }
      if (!disposed) timer = setTimeout(poll, 3000);
    };
    void poll();
    return () => { disposed = true; if (timer) clearTimeout(timer); };
  }, [minted, seen, onChanged, onConnected, tool.id]);


  // THE COACH: with the extension in this browser, Claude and ChatGPT can be
  // set up on their own page, with a card that points and fills the link in
  const ext = useExtension();
  const coachTool = COACH_FOR_TOOL[tool.id];
  const canCoach = !!coachTool && ext.canCoach(coachTool);
  const [coachNote, setCoachNote] = useState<string | null>(null);
  const startCoach = useCallback(async () => {
    if (!coachTool) return;
    const ok = await ext.coach(coachTool, endpoint);
    setCoachNote(ok
      ? `${tool.name} is open in a new tab. the card in its corner shows each step, and fills in the link for you.`
      : "the extension did not answer. the steps below work just as well.");
    trackFunnel(EVENTS.connect_instructions_copied, { what: ok ? "coach" : "coach_failed", flow: FLOW });
  }, [coachTool, ext, endpoint, tool.name]);

  const g = tool.guide ?? {};
  const say = `say hi to ${toolName} in a new chat. it shows up here the moment it calls`;

  // the thing to copy now sits above the steps, so a step that says "below" says "above"
  const connectItems: React.ReactNode[] = [
    ...tool.steps.map((s) => s.split(MCP_URL).join(endpoint).replace(/\bbelow\b/g, "above")),
    say,
  ];
  const copyLine =
    deeplink ? `one press opens ${tool.name} with innernet filled in. no ${tool.name} here? copy this instead.`
    : tool.paste === "command" ? "the one command"
    : tool.paste === "block" ? `the block ${toolName} reads, with your link inside`
    : `the link to paste into ${toolName}`;
  const door = deeplink ? (
    <a className="cm-open" href={deeplink} onClick={() => trackFunnel(EVENTS.connect_instructions_copied, { what: "deeplink", flow: FLOW })}>
      open in {tool.name}
      <span className="cm-next-badge" aria-hidden><Chevron size={13} /></span>
    </a>
  ) : null;
  // the snippet, and under it exactly where it goes, so the person never has
  // to guess a file or a key (a guessed key is how Antigravity's door was
  // never called).
  const block = (
    <div className="cg-fallback" style={{ marginTop: 0 }}>
      <CopyBlock text={config} mono={tool.paste !== "link"} whole={tool.paste === "block"} what="connection_endpoint" label={tool.configLabel} />
      {tool.where && <span className="cg-fallback-line">it goes in {tool.where}</span>}
    </div>
  );
  // what this connection remembers about the client on the other end, once
  // one has called (lib/connect/memory.ts).
  const known = current ? memoryLine(current.memory) : null;
  const why =
    tool.paste === "block" ? (
      <>{toolName} reads a file, not a link. this block carries innernet’s link under the key it reads; paste it whole. you sign in with your browser, and its first call takes the name <strong>{name}</strong>.</>
    ) : tool.paste === "command" ? (
      <>one line in a terminal. it signs in with your browser, no key is ever pasted, and its first call takes the name <strong>{name}</strong>.</>
    ) : (
      <>one link for every ai, and not a key: you sign in with your browser. its first call takes the name <strong>{name}</strong>.</>
    );

  const beginImport = () => {
    setImporting(true);
    // hand over: this sheet leaves, the import window takes the room
    setOpen(false);
  };

  const subjects: Subject[] = [
    {
      key: "name",
      // once it has a name, the head says which connection this is
      title: current ? "" : `name this ${tool.generic ? "connection" : tool.name}`,
      why: "one name per connection, so you can tell your laptop from your work machine.",
      body: (
        <Field
          label="what to call it"
          value={label}
          autoFocus={!minted}
          maxLength={80}
          error={error}
          onChange={(e) => { setLabel(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") void save().then((ok) => { if (ok) setSection("mcp"); }); }}
        />
      ),
      primary: {
        label: current && label.trim() !== current.label ? "save" : "next",
        onClick: () => void save().then((ok) => { if (ok) setSection("mcp"); }),
        loading: busy,
      },
    },
    {
      key: "mcp",
      title: `connect ${tool.generic ? "it" : tool.name}`,
      why,
      // WHAT TO COPY FIRST, HOW SECOND (John, 2026-10-21): the thing that goes
      // into the tool leads, the one paste for the ai under it, and the numbered
      // steps (with their pictures) close the subject for whoever wants them.
      body: (
        <>
          {canCoach && (
            <section className="cg-copy">
              <button type="button" className="cm-open" style={{ alignSelf: "flex-start" }} onClick={() => void startCoach()}>
                set it up in {tool.name} for me
                <span className="cm-next-badge" aria-hidden><Chevron size={13} /></span>
              </button>
              <span className="cg-copy-line">
                {coachNote ?? `opens ${tool.name} with a card that points at each step and fills the link in.`}
              </span>
            </section>
          )}
          <section className="cg-copy">
            <span className="cg-copy-line">{copyLine}</span>
            {door}
            {block}
          </section>
          <section className="cg-copy">
            <span className="cg-copy-line">
              {wiresItself(tool.id)
                ? `or paste this into ${toolName}, and it wires itself in.`
                : `or paste this into ${toolName}, and it walks you through the steps.`}
            </span>
            <CopyBlock text={pasteText(endpoint)} mono={false} what="one_paste" label="for your ai" />
          </section>
          <section className="cg-how" aria-label="step by step">
            <span className="cg-how-head">step by step</span>
            <GuideSteps items={connectItems} shots={g.mcp} />
          </section>
          {known && <p className="cg-aside-line" style={{ marginTop: "1.2rem" }}>last time it called: {known}.</p>}
        </>
      ),
      primary: seen
        ? { label: "next", onClick: () => setSection("permissions"), done: true }
        : { label: "i've done this", onClick: () => setSection("permissions") },
    },
    {
      key: "permissions",
      title: "let it work on its own",
      why: `allow innernet once, so ${toolName} never stops to ask in the middle of a conversation.`,
      body: <GuideSteps items={tool.trust ?? GENERIC_TRUST} shots={g.permissions} />,
      primary: { label: "next", onClick: () => setSection("instructions") },
    },
    {
      key: "instructions",
      title: "make it remember you",
      why: `paste this once, and ${toolName} loads you at the start of every chat.`,
      body: (
        <>
          <section className="cg-copy">
            <span className="cg-copy-line">paste it into {tool.promptWhere}, once.</span>
            <CopyBlock text={PROMPT_SYNC} mono={false} what="prompt_sync" label="instruction" />
          </section>
          <section className="cg-copy">
            <span className="cg-copy-line">optional: paste this in a chat about something you&rsquo;re building, and its folder appears here.</span>
            <CopyBlock text={PROMPT_PROJECT} mono={false} what="prompt_project" label="first project" />
          </section>
          <section className="cg-how" aria-label="step by step">
            <span className="cg-how-head">step by step</span>
            <GuideSteps
              items={["copy the instruction above", `open ${tool.promptWhere}`, "paste it and save"]}
              shots={g.instructions}
            />
          </section>
        </>
      ),
      primary: { label: "next", onClick: () => setSection("import") },
    },
    {
      key: "import",
      title: "bring your memory in",
      why: `bring what ${toolName} already knows, so every tool starts from the same place.`,
      body: (
        <>
          <GuideSteps items={tool.importSteps ?? GENERIC_IMPORT} shots={g.import} />
          <div className="cg-acts">
            <button type="button" className="cm-open" onClick={beginImport}>
              bring it in now
              <span className="cm-next-badge" aria-hidden><Chevron size={13} /></span>
            </button>
          </div>
        </>
      ),
      skip: current ? undefined : { label: "skip for now", onClick: close },
      primary: current
        ? { label: "settings", onClick: () => setSection("settings") }
        : { label: "done", onClick: close, done: true },
    },
    ...(current
      ? [
          {
            key: "settings" as const,
            title: "settings",
            why: `what ${name} may read of you, what it has been handed, and how it reaches you.`,
            body: <ConnectionSettings connection={current} name={name} ring={ring} now={now} onLetGo={() => void letGo()} />,
            primary: { label: "done", onClick: close, done: true },
          },
        ]
      : []),
  ];

  const at = Math.max(0, subjects.findIndex((s) => s.key === section));
  const s = subjects[at];
  const back = at > 0 ? subjects[at - 1] : null;

  // the navigator: the subjects as words. No steps between this tool's other
  // connections here: ← → belong to the subjects, and a second pair of arrows
  // beside the foot's back and next only asked which "next" was meant. The
  // connection's id is still the step key, so a new one arriving slides in.
  const nav: MiniNavProps = {
    crumb: [
      { label: "connections", onClick: close },
      ...(tool.generic ? [] : [{ label: tool.name, onClick: current ? () => setSection("name") : undefined }]),
      { label: current ? name : "new" },
    ],
    titleId,
    sections: subjects.map((x) => ({ id: x.key, label: x.key === "mcp" ? "connect" : PHASES.find((p) => p.key === x.key)?.label ?? x.key })),
    section: s.key,
    onSection: (id) => go(id as Phase),
    stepKey: current?.id ?? "new",
  };

  const state: ConnState | null = current ? connState(current, now) : null;

  // The navigator already says the subject's word: its title is shown only when
  // it adds something (law_say_it_once).
  const navWord = nav.sections?.find((x) => x.id === s.key)?.label ?? "";
  const showTitle = !!s.title.trim() && s.title.trim().toLowerCase() !== navWord.trim().toLowerCase();

  // A connection is private: no door of innernet's opens it, so there is no
  // `share ↗`. The one thing the sheet already shows and lets you copy (the
  // connect step's link, innernet's one link) is offered in `options`
  // wherever you are in it.
  const share: SheetShare | null = minted
    ? {
        name,
        items: [
          {
            label: "copy the link",
            onSelect: () => {
              // the note is the Noticer's word for what happened, so it says what DID
              void copyText(endpoint).then((ok) => {
                const said = ok ? "copied innernet’s link" : COPY_FAILED_LINE;
                window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: said } }));
                if (ok) trackFunnel(EVENTS.connect_instructions_copied, { what: "connection_endpoint", flow: FLOW });
              });
            },
          },
        ],
      }
    : null;

  return (
    <>
      <EtherModal
        open={open}
        onClose={close}
        labelledBy={titleId}
        size="lg"
        className="cw-sheet"
        nav={nav}
        share={share}
        header={
          // the connection, once and small, seated at the top: the tool's mark,
          // its name, its state beside it. The navigator under it carries the
          // weight of the head.
          <div className="cw-head">
            <BrandAvatar name={tool.icon_key} size={22} className="cw-tile" />
            <div className="cg-title">
              <h2 id={titleId} className="eth-title">{name}</h2>
              {state && (
                <span className="cg-state" data-state={state}>
                  <span className="cg-state-dot" aria-hidden />
                  <span className="cg-state-word">{STATE_WORD[state]}</span>
                </span>
              )}
            </div>
          </div>
        }
        footer={
          <div className="cw-foot">
            {back && (
              <button type="button" className="quiet-link" onClick={() => go(back.key)}>
                back
              </button>
            )}
            {s.skip && (
              <button type="button" className="quiet-link" onClick={s.skip.onClick}>
                {s.skip.label}
              </button>
            )}
            {s.primary && (
              <PillButton
                onClick={s.primary.onClick}
                loading={s.primary.loading}
                disabled={s.primary.loading || undefined}
                icon={s.primary.done ? <Check /> : undefined}
              >
                {s.primary.label}
              </PillButton>
            )}
          </div>
        }
      >
        <div className="cw cw-body">
          {showTitle && <div className="cm-steptitle">{s.title}</div>}
          {s.why && <p className="cm-why">{s.why}</p>}
          {s.body}
        </div>
      </EtherModal>
      {importing && <ImportSteps onClose={onClose} openedFrom="connect_guide" />}
    </>
  );
}
